import i18n from 'i18next'
import { useCallback, useRef, useState } from 'react'
import { fetchSessionMessages, sendAgentMessage } from '../api/agent.api'
import type {
  AgentChatContext,
  AgentMessageFromDB,
  AgentSSEEvent,
  ChatMessage,
  ToolCallResult,
} from '../types/agent.types'
import { parseSSEStream } from '../utils/sse-parser'

/**
 * `getContext` (ADDITIVE, OPTIONAL): resolves the copilot context fresh on every send so the
 * agent always sees the latest draft. When omitted, requests carry no context and the agent
 * behaves exactly as the default Sergeo assistant.
 */
export function useAgentChat(opts?: {
  onSessionCreated?: () => void
  getContext?: () => AgentChatContext | undefined
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const onSessionCreatedRef = useRef(opts?.onSessionCreated)
  onSessionCreatedRef.current = opts?.onSessionCreated
  const getContextRef = useRef(opts?.getContext)
  getContextRef.current = opts?.getContext

  const handleSSEEvent = useCallback((event: AgentSSEEvent, assistantId: string) => {
    switch (event.type) {
      case 'session': {
        const isNew = event.sessionId !== sessionIdRef.current
        setSessionId(event.sessionId)
        sessionIdRef.current = event.sessionId
        if (isNew) onSessionCreatedRef.current?.()
        break
      }

      case 'text_delta':
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId ? { ...msg, text: msg.text + event.text } : msg,
          ),
        )
        break

      case 'tool_start': {
        const toolCall: ToolCallResult = {
          tool: event.tool,
          toolUseId: event.toolUseId,
          status: 'running',
          input: event.input,
        }
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? { ...msg, toolCalls: [...msg.toolCalls, toolCall] }
              : msg,
          ),
        )
        break
      }

      case 'tool_result':
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? {
                  ...msg,
                  toolCalls: msg.toolCalls.map((tc) =>
                    tc.toolUseId === event.toolUseId
                      ? { ...tc, status: 'complete' as const, result: event.result }
                      : tc,
                  ),
                }
              : msg,
          ),
        )
        break

      case 'done':
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? {
                  ...msg,
                  id: event.messageId,
                  isStreaming: false,
                  toolCalls: msg.toolCalls.map((tc) =>
                    tc.status === 'running' ? { ...tc, status: 'complete' as const } : tc,
                  ),
                }
              : msg,
          ),
        )
        break

      case 'error':
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? { ...msg, text: msg.text || event.message, isStreaming: false }
              : msg,
          ),
        )
        break
    }
  }, [])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return

    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      text,
      toolCalls: [],
      isStreaming: false,
      createdAt: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, userMessage])

    const assistantId = `temp-assistant-${Date.now()}`
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      text: '',
      toolCalls: [],
      isStreaming: true,
      createdAt: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, assistantMessage])

    setIsStreaming(true)
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const context = getContextRef.current?.()
      const response = await sendAgentMessage(
        text,
        sessionId ?? undefined,
        controller.signal,
        context,
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      for await (const event of parseSSEStream(response)) {
        handleSSEEvent(event, assistantId)
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? { ...msg, text: msg.text || i18n.t('agent.errors.generic'), isStreaming: false }
            : msg,
        ),
      )
    } finally {
      setIsStreaming(false)
      abortControllerRef.current = null
      // Clear message-level isStreaming and any stuck tool call spinners
      setMessages((prev) =>
        prev.map((msg) =>
          msg.isStreaming || msg.toolCalls.some((tc) => tc.status === 'running')
            ? {
                ...msg,
                isStreaming: false,
                toolCalls: msg.toolCalls.map((tc) =>
                  tc.status === 'running' ? { ...tc, status: 'complete' as const } : tc,
                ),
              }
            : msg,
        ),
      )
    }
  }, [isStreaming, sessionId, handleSSEEvent])

  const loadSession = useCallback(async (loadSessionId: string) => {
    try {
      const dbMessages = await fetchSessionMessages(loadSessionId)
      const chatMessages = mapDbMessagesToChatMessages(dbMessages)
      setMessages(chatMessages)
      setSessionId(loadSessionId)
      sessionIdRef.current = loadSessionId
    } catch (error) {
      console.error('Failed to load session:', error)
    }
  }, [])

  const startNewChat = useCallback(() => {
    abortControllerRef.current?.abort()
    setMessages([])
    setSessionId(null)
    sessionIdRef.current = null
    setIsStreaming(false)
  }, [])

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort()
    setIsStreaming(false)
  }, [])

  return {
    messages,
    sessionId,
    isStreaming,
    sendMessage,
    loadSession,
    startNewChat,
    cancel,
  }
}

function mapDbMessagesToChatMessages(dbMessages: AgentMessageFromDB[]): ChatMessage[] {
  const chatMessages: ChatMessage[] = []

  for (let i = 0; i < dbMessages.length; i++) {
    const msg = dbMessages[i]
    if (!msg) continue
    const contentBlocks = msg.content as Array<{
      type: string
      text?: string
      id?: string
      name?: string
      input?: unknown
      content?: string
      tool_use_id?: string
    }>

    if (!Array.isArray(contentBlocks)) continue

    if (msg.role === 'user') {
      const textParts = contentBlocks
        .filter((b) => b.type === 'text')
        .map((b) => b.text ?? '')
        .join('')

      if (textParts) {
        chatMessages.push({
          id: msg.id,
          role: 'user',
          text: textParts,
          toolCalls: [],
          isStreaming: false,
          createdAt: msg.createdAt,
        })
      }
      continue
    }

    // Assistant message
    const text = contentBlocks
      .filter((b) => b.type === 'text')
      .map((b) => b.text ?? '')
      .join('')

    const toolUseBlocks = contentBlocks.filter((b) => b.type === 'tool_use')

    const nextMsg = dbMessages[i + 1]
    const nextContentBlocks =
      nextMsg?.role === 'user' && Array.isArray(nextMsg.content)
        ? (nextMsg.content as Array<{ type: string; tool_use_id?: string; content?: string }>)
        : []

    const toolCalls: ToolCallResult[] = toolUseBlocks.map((toolUse) => {
      const matchingResult = nextContentBlocks.find(
        (b) => b.type === 'tool_result' && b.tool_use_id === toolUse.id,
      )

      if (matchingResult) {
        let parsedResult: unknown = matchingResult.content
        try {
          if (typeof matchingResult.content === 'string') {
            parsedResult = JSON.parse(matchingResult.content)
          }
        } catch {
          /* keep as string */
        }

        return {
          tool: toolUse.name ?? 'unknown',
          toolUseId: toolUse.id ?? '',
          status: 'complete' as const,
          input: toolUse.input,
          result: parsedResult,
        }
      }

      return {
        tool: toolUse.name ?? 'unknown',
        toolUseId: toolUse.id ?? '',
        status: 'error' as const,
        input: toolUse.input,
        result: null,
      }
    })

    chatMessages.push({
      id: msg.id,
      role: 'assistant',
      text,
      toolCalls,
      isStreaming: false,
      createdAt: msg.createdAt,
    })
  }

  return chatMessages
}
